import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Star, MessageSquare, BadgeCheck } from 'lucide-react';
import { API } from '../lib/api';
import ReviewModal from './ReviewModal';

const StarRow = ({ value }) => (
  <div className="flex" aria-label={`${value} of 5 stars`}>
    {[1,2,3,4,5].map((n) => (
      <Star key={n} className={`w-4 h-4 ${n <= value ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
    ))}
  </div>
);

const ReviewsList = ({ targetType, targetId, targetName }) => {
  const [data, setData] = useState({ items: [], count: 0, avg: 0 });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/reviews`, { params: { target_type: targetType, target_id: targetId } });
      setData(r.data || { items: [], count: 0, avg: 0 });
    } catch { setData({ items: [], count: 0, avg: 0 }); }
    finally { setLoading(false); }
  }, [targetType, targetId]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`reviews-section-${targetType}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-[#ee5e23]" /> Buyer Reviews
          </h2>
          {data.count > 0 ? (
            <div className="flex items-center gap-2 text-sm text-gray-700 mt-1">
              <StarRow value={Math.round(data.avg)} />
              <span className="font-semibold">{data.avg}</span>
              <span className="text-gray-500">· {data.count} review{data.count !== 1 ? 's' : ''}</span>
            </div>
          ) : (
            <p className="text-sm text-gray-500 mt-1">No reviews yet — be the first!</p>
          )}
        </div>
        <Button onClick={() => setModalOpen(true)} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid={`write-review-btn-${targetType}`}>
          <Star className="w-4 h-4 mr-1" /> Write a review
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Loading reviews...</div>
      ) : data.items.length === 0 ? null : (
        <ul className="space-y-3">
          {data.items.map((r) => (
            <li key={r.id} className="border-t border-gray-100 pt-3" data-testid={`review-item-${r.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-900 text-sm">{r.reviewer_name}</span>
                  {r.verified && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-1.5 py-0.5"
                      title="Verified Account: this reviewer has a registered TelanganaMart account."
                      data-testid={`review-verified-${r.id}`}
                    >
                      <BadgeCheck className="w-3 h-3" /> Verified Account
                    </span>
                  )}
                  <span className="text-[11px] text-gray-500">+91 {r.mobile}</span>
                </div>
                <div className="flex items-center gap-2">
                  <StarRow value={r.stars} />
                  <span className="text-[11px] text-gray-500">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              {r.text && <p className="text-sm text-gray-700 mt-1.5 leading-relaxed">{r.text}</p>}
            </li>
          ))}
        </ul>
      )}

      <ReviewModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        targetType={targetType}
        targetId={targetId}
        targetName={targetName}
        onSubmitted={load}
      />
    </section>
  );
};

export default ReviewsList;
